import React, { useState, useEffect } from 'react';
import {
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
  Stack,
  Alert,
  alpha,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import Swal from 'sweetalert2';
import { Modal } from '../../components/BootstrapToMUI';
import request from '../../services/Request';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CheckIcon from '@mui/icons-material/Check';

export default function ReceiptModal(props) {
  const theme = useTheme();

  const getSSURL = `/receiptByEmail/`;
  const updatSSURL = `/machines/updateEmail`;
  const { rowData, showAdmin, setRowData, setMachines, ...rest } = props;

  const [ss, setSS] = useState([]);
  const [ssid, setSsid] = useState('');
  const [ssErr, setssErr] = useState('');

  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    if (props.show) {
      setIsReady(false);
      let isCancelled = false;
      const belongTo =
        rowData && rowData.length > 0 ? (rowData[0].belongTo ? rowData[0].belongTo : '') : '';
      if (belongTo != '') {
        setSsid(
          rowData && rowData.length > 0 ? (rowData[0].receipt ? rowData[0].receipt : '') : ''
        );
        setssErr('');
        //getAllEmployees
        async function getSS() {
          request({
            method: 'get',
            url: getSSURL + belongTo,
          })
            .then((res) => {
              if (!isCancelled) {
                setSS(res.data);
                setIsReady(true);
              }
            })
            .catch((err) => {
              setssErr('Failed to get receipt templates');
              setIsReady(true);
            });
        }

        getSS();

        return () => {
          isCancelled = true;
        };
      }
    }
  }, [props.show]);

  const onSubmit = async (data) => {
    let finalID = ssid;
    if (ssid === 'None') finalID = '';
    console.log('finalID', finalID);
    let machineIDs = rowData.map((e) => e._id);
    if (showAdmin) {
      request({
        method: 'patch',
        url: updatSSURL,
        data: {
          id: finalID,
          machineID: rowData.map((e) => e._id),
        },
      })
        .then((res) => {
          setssErr('');
          setMachines((prevMachines) =>
            prevMachines.map((e) => (machineIDs.includes(e._id) ? { ...e, receipt: finalID } : e))
          );
          setRowData((prevRowData) =>
            prevRowData.map((e) => ({
              ...e,
              receipt: finalID,
            }))
          );
          Swal.fire({
            text: `The receipt template has been selected`,
            icon: 'success',
          }).then(() => {
            props.onHide();
          });
        })
        .catch((err) => {
          if (err.response) {
            if (err.response.status === 404) {
              setssErr(err.response.data.message);
            }
          }
        });
    } else {
      Swal.fire({
        text: `You have no permission to change the receipt template`,
        icon: 'error',
      });
    }
  };

  const handleChange = async (event) => {
    setssErr('');
    setSsid(event.target.value);
  };

  return (
    <Modal {...rest} centered size="sm">
      <Modal.Header closeButton>
        <Modal.Title id="contained-modal-title-vcenter">
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            Select Receipt Template
          </Typography>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Box sx={{ p: 3 }}>
          <Stack spacing={3}>
            {/* Main Receipt Template Selection */}
            <Box
              sx={{
                p: 3,
                borderRadius: 3,
                background: alpha(theme.palette.primary.main, 0.05),
                border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
              }}
            >
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="flex-start">
                <Box sx={{ flex: 1, width: '100%' }}>
                  <FormControl fullWidth>
                    <InputLabel id="receipt-template-label">Select Receipt Template</InputLabel>
                    <Select
                      labelId="receipt-template-label"
                      id="receipt-template-select"
                      value={ssid}
                      onChange={handleChange}
                      label="Select Receipt Template"
                      sx={{
                        borderRadius: 2,
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: alpha(theme.palette.primary.main, 0.3),
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: theme.palette.primary.main,
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: theme.palette.primary.main,
                          borderWidth: 2,
                        },
                      }}
                    >
                      <MenuItem value={'None'}>None</MenuItem>
                      {ss.map((m) => (
                        <MenuItem key={m._id} value={m._id}>
                          {m.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {ssErr != '' && (
                    <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>
                      {ssErr}
                    </Alert>
                  )}
                </Box>
                <Button
                  disabled={ssid == '' || ssid == 'None'}
                  onClick={() => {
                    window.open(
                      `/emailPreview/${ssid}/${rowData[0]?._id}`,
                      'mywin',
                      'width=1080,height=1920'
                    );
                  }}
                  variant="outlined"
                  startIcon={<VisibilityIcon />}
                  sx={{
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    px: 3,
                    py: 1.5,
                    minWidth: 140,
                    borderWidth: 1.5,
                    '&:hover': {
                      borderWidth: 1.5,
                      backgroundColor: alpha(theme.palette.primary.main, 0.08),
                    },
                  }}
                >
                  Preview
                </Button>
              </Stack>
            </Box>

            {/* Select Button */}
            <Button
              fullWidth
              onClick={onSubmit}
              disabled={ssid == '' || !isReady}
              variant="contained"
              startIcon={<CheckIcon />}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 700,
                py: 1.5,
                fontSize: '1rem',
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.4)}`,
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.5)}`,
                },
                '&:disabled': {
                  background: alpha(theme.palette.action.disabled, 0.12),
                  color: theme.palette.action.disabled,
                },
                transition: 'all 0.3s ease',
              }}
            >
              Select Receipt Template
            </Button>
          </Stack>
        </Box>
      </Modal.Body>
    </Modal>
  );
}
